import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, delay, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { User } from '@features/users/models/user';
import { JwtDecoderService } from '../services/jwt-decoder.service';
import { AuthService } from '../services/auth.service';
import { ApiConstant } from '@shared/constants/api.constant';
import { JwtInterceptor } from '@auth0/angular-jwt';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { PasswordResetType } from '@shared/enums/password-reset-type.enum';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private readonly isHandling428$ = new BehaviorSubject<boolean>(false);

  constructor(
    private injector: Injector,
    private router: Router,
    private jwtInterceptor: JwtInterceptor
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // HANDLE 428 – Password Expired
        if (error instanceof HttpErrorResponse && error.status === 428) {
          return this.handle428Error(error);
        }

        // HANDLE 401 – Refresh Token failed
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          request.url.includes(ApiConstant.REFRESH_TOKEN)
        ) {
          const jwtDecoderService = this.injector.get(JwtDecoderService);
          jwtDecoderService.removeCurrentToken();
          location.reload();
          return throwError(error);
        }

        // HANDLE 401 – Normal Access Token expired
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next);
        }

        // Other errors → forward to ErrorHandlerInterceptor
        return throwError(error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const authService = this.injector.get(AuthService);

      return authService.refreshTheToken().pipe(
        switchMap((user: User) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(user.refreshToken);
          return this.jwtInterceptor.intercept(request, next);
        }),
        catchError((err) => {
          // Reset both, or the next 401 waits forever on a subject that will never emit and
          // the guards bounce the user back in on a token that can no longer be refreshed.
          this.isRefreshing = false;
          this.injector.get(JwtDecoderService).removeCurrentToken();
          this.router.navigate([RoutesUtil.AuthLogin.url()]);
          return throwError(err);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token != null),
      take(1),
      delay(250),
      switchMap(() => this.jwtInterceptor.intercept(request, next)),
      catchError((err) => throwError(err))
    );
  }

  // handle password expired
  private handle428Error(error: HttpErrorResponse) {
    const authService = this.injector.get(AuthService);

    if (this.isHandling428$.value) {
      return this.isHandling428$.pipe(
        filter(isHandling => !isHandling),
        take(1),
        switchMap(() => throwError(() => error))
      );
    }

    this.isHandling428$.next(true);

    const logoutOrRedirect$ = authService.isLoggedIn()
      ? authService.logout(true, false)
      : of(null).pipe(
          tap(() =>
            this.router.navigate([RoutesUtil.AuthResetPasswordBy.url()], {
              queryParams: { resetType: PasswordResetType.Reset },
            })
          )
        );

    return logoutOrRedirect$.pipe(
      take(1),
      switchMap(() => throwError(() => error)),
      finalize(() => this.isHandling428$.next(false))
    );
  }
}
